// client/src/components/pages/LexiStar/Reports/TripReport.tsx
//
// /lexistar/reports/trip/:trip_id — the trip report page (ST-00188 M4-R2;
// m4-contracts §7; FR-WB-010; LS-09 §5.5). Page permission
// lexistar.reports.view (config/pagePermissions); the parts behind their own
// keys (replay, cost, messages) say so when the viewer lacks them.
//
// Screen: the document on a wide sheet with the interactive map, and a toolbar
// — back to the trip, [PDF] (runs `trip_report` and downloads the renderer's
// PDF), share link, and the print layout. `?print=1`: the A4 layout the
// renderer prints (no toolbar in the renderer; page breaks; the map as SVG).

import { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { FaArrowLeft, FaLink, FaPrint, FaDesktop } from 'react-icons/fa';
import TripReportDocument from './TripReportDocument';
import { loadTripReport, type TripReportData } from './tripReportData';
import { errorText, statusOf } from '../../../../services/lexistarReports';
import {
  ExportButtons,
  PaperMessage,
  ReportFrame,
  ReportRenderContext,
  toolbarButton,
  toolbarPrimary,
  usePrintMode,
  type LoadState,
} from './reportAtoms';
import { useAuth } from '../../../../types/commonComponents';

export default function TripReport() {
  const { trip_id: tripId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const print = usePrintMode();
  const rendering = useContext(ReportRenderContext);
  const { hasPermission } = useAuth();
  const canView = hasPermission('lexistar.reports.view');
  const canReplay = hasPermission('lexistar.replay.view');
  const canCost = hasPermission('lexistar.costs.view');
  const canMessages = hasPermission('lexistar.messages.view');
  const showRand = hasPermission('lexistar.costs.view') || hasPermission('lexistar.fuel.manage');
  const [state, setState] = useState<{ load: LoadState; data: TripReportData | null; error: string | null; at: string | null }>({
    load: 'loading',
    data: null,
    error: null,
    at: null,
  });
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!canView || !tripId) return;
    setState((s) => ({ ...s, load: 'loading', error: null }));
    try {
      const data = await loadTripReport(tripId, { canReplay, canCost, canMessages });
      setState({ load: 'ready', data, error: null, at: new Date().toISOString() });
    } catch (err) {
      setState({
        load: 'error',
        data: null,
        at: null,
        error:
          statusOf(err) === 404
            ? 'Trip not found, or not visible to your organisation.'
            : errorText(err, 'The trip report could not be loaded.'),
      });
    }
  }, [canView, tripId, canReplay, canCost, canMessages]);

  useEffect(() => {
    load();
  }, [load]);

  const reference = state.data ? String(state.data.trip.reference || '') : '';
  useEffect(() => {
    if (reference) document.title = `Trip report ${reference} · LexiStar`;
  }, [reference]);

  const setPrint = (on: boolean) => {
    const next = new URLSearchParams(params);
    if (on) next.set('print', '1');
    else next.delete('print');
    setParams(next, { replace: true });
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href.replace(/[?&]print=1/, ''));
      setNotice('Link copied.');
    } catch {
      setNotice(window.location.href);
    }
  };

  if (!canView) {
    return (
      <ReportFrame print={print} state="error" error="forbidden">
        <PaperMessage print={print} title="You do not have permission to view reports." />
      </ReportFrame>
    );
  }

  const toolbar = rendering ? null : (
    <>
      <Link to={`/lexistar/trips/${tripId}`} className="inline-flex items-center gap-1.5 text-sm text-slate-700 hover:text-slate-900">
        <FaArrowLeft size={10} /> Back to the trip
      </Link>
      <div className="flex flex-wrap items-center gap-2">
        {notice && (
          <span role="status" className="text-xs text-slate-600">
            {notice}
          </span>
        )}
        <button type="button" className={toolbarButton} onClick={copyLink} aria-label="Copy link">
          <FaLink size={11} /> Share
        </button>
        {print ? (
          <>
            <button type="button" className={toolbarButton} onClick={() => setPrint(false)}>
              <FaDesktop size={11} /> Screen view
            </button>
            <button type="button" className={toolbarButton} onClick={() => window.print()}>
              <FaPrint size={11} /> Print
            </button>
          </>
        ) : (
          <button type="button" className={toolbarButton} onClick={() => setPrint(true)}>
            <FaPrint size={11} /> Print layout
          </button>
        )}
        <ExportButtons
          reportKey="trip_report"
          parameters={{ trip_id: tripId }}
          formats={['pdf']}
          disabled={state.load !== 'ready'}
          filenameBase={reference ? `trip-report-${reference}` : undefined}
          buttonClassName={toolbarPrimary}
        />
      </div>
    </>
  );

  return (
    <ReportFrame print={print} state={state.load} error={state.error} toolbar={toolbar}>
      {state.load === 'loading' && !state.data ? (
        <PaperMessage print={print} title="Loading the trip report…" />
      ) : state.error || !state.data ? (
        <PaperMessage print={print} title={state.error || 'The trip report could not be loaded.'} />
      ) : (
        <TripReportDocument data={state.data} print={print} showRand={showRand} generatedAt={state.at} />
      )}
    </ReportFrame>
  );
}
